import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useColor4 } from '../hooks/useColor4';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShield } from '@fortawesome/free-solid-svg-icons/faShield';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons/faShieldHalved';

/**
 * "Built to Last: Steel vs. Wood" — two benefit cards for the Better-Built
 * Living page: The Steel Advantage and Enhanced Protection.
 *
 * Icons are structural and baked in; copy is fully editable.
 */

export function BetterBuiltSteelVsWood() {
  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 140 });
  const color4 = useColor4();

  return (
    <section className="w-full py-14 md:py-20 bg-[var(--vibe-color-background)]" style={color4?.primaryOverride}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headingRef} className="reveal-fade-up text-center mb-10 md:mb-14 max-w-3xl mx-auto">
          <RichTextContent
            path="betterbuiltsteelvswood.title"
            label="Section Title"
            defaultValue="<p>Built to Last: Steel vs. Wood</p>"
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal text-[var(--vibe-color-text)] leading-tight tracking-tight"
          />
          <div className="w-16 h-1 mx-auto mt-5 rounded-full bg-[var(--vibe-color-primary)]" />
        </div>

        <div
          ref={gridRef}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
        >
          {/* Card 1 — The Steel Advantage */}
          <article
            data-reveal
            className="reveal-fade-up bbsw-card bg-white border border-[var(--vibe-color-border)] p-7 sm:p-8 flex flex-col"
          >
            <span className="bbsw-icon mb-5 inline-flex items-center justify-center">
              <FontAwesomeIcon icon={faShield} className="bbsw-icon-glyph" aria-hidden="true" />
            </span>
            <RichTextContent
              path="betterbuiltsteelvswood.card1_title"
              defaultValue="<p>The Steel Advantage</p>"
              as="h3"
              className="font-heading text-xl sm:text-2xl font-normal text-[var(--vibe-color-text)] mb-3 tracking-tight"
            />
            <RichTextContent
              path="betterbuiltsteelvswood.card1_body"
              defaultValue="<p>Unlike wood, steel resists warping and cracking, offering long-lasting durability and minimal maintenance.</p>"
              as="div"
              className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-0"
            />
          </article>

          {/* Card 2 — Enhanced Protection */}
          <article
            data-reveal
            className="reveal-fade-up bbsw-card bg-white border border-[var(--vibe-color-border)] p-7 sm:p-8 flex flex-col"
          >
            <span className="bbsw-icon mb-5 inline-flex items-center justify-center">
              <FontAwesomeIcon icon={faShieldHalved} className="bbsw-icon-glyph" aria-hidden="true" />
            </span>
            <RichTextContent
              path="betterbuiltsteelvswood.card2_title"
              defaultValue="<p>Enhanced Protection</p>"
              as="h3"
              className="font-heading text-xl sm:text-2xl font-normal text-[var(--vibe-color-text)] mb-3 tracking-tight"
            />
            <RichTextContent
              path="betterbuiltsteelvswood.card2_body"
              defaultValue="<p>Steel is non-combustible and less vulnerable to pests, offering enhanced protection for your home.</p>"
              as="div"
              className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-0"
            />
          </article>
        </div>
      </div>

      <style>{`
        .bbsw-card {
          border-radius: 4px;
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .bbsw-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 30px -10px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 50%, var(--vibe-color-border));
        }
        .bbsw-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          color: var(--vibe-color-primary);
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
        }
        .bbsw-icon-glyph {
          width: 26px;
          height: 26px;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'BetterBuiltSteelVsWood',
  displayName: 'Better-Built Steel vs. Wood',
  tokens: {
    'betterbuiltsteelvswood.title': {
      label: 'Section Title',
      type: 'richtext',
      defaultValue: '<p>Built to Last: Steel vs. Wood</p>',
    },
    'betterbuiltsteelvswood.card1_title': {
      label: 'Card 1 Title',
      type: 'richtext',
      defaultValue: '<p>The Steel Advantage</p>',
    },
    'betterbuiltsteelvswood.card1_body': {
      label: 'Card 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Unlike wood, steel resists warping and cracking, offering long-lasting durability and minimal maintenance.</p>',
    },
    'betterbuiltsteelvswood.card2_title': {
      label: 'Card 2 Title',
      type: 'richtext',
      defaultValue: '<p>Enhanced Protection</p>',
    },
    'betterbuiltsteelvswood.card2_body': {
      label: 'Card 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Steel is non-combustible and less vulnerable to pests, offering enhanced protection for your home.</p>',
    },
  },
};

(BetterBuiltSteelVsWood as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default BetterBuiltSteelVsWood;
